import { Popup } from 'components/Popup'
import { Typography } from 'components/Typography'
import { useBankAccountPopup } from './Providers/BankAccountPopupProvider'
import { BUTTON_TYPES, Button } from 'components/Button'
import { BTN_TXT, LABELS, VALIDATION_MESSAGES } from 'constants/txt'
import { useCallback, useState } from 'react'
import { Input } from 'components/Input'
import { validateMessages } from 'helper/common'
import { Form } from 'antd'
import { TVerifyDepositForm, TVerifyDepositFormErrors } from './types'
import { CustomForm } from 'components/Forms/CustomForm'
import { twoDecimalParser } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { AxiosResponse } from 'axios'
import { setLoading } from 'redux/store/common/slice'
import { useDispatch } from 'react-redux'
import { verifyDeposit } from './api'
import { Banner } from 'components/Banner/Banner'
import { notification } from 'components/Notification'

export const VerifyDeposit = ({ onSuccess }: { onSuccess: () => void }) => {
  const {
    verifyDepositsPopup: { state, actions }
  } = useBankAccountPopup()

  const { account_number, business_account_id, uuid } = state.payload

  const [form] = Form.useForm<TVerifyDepositForm>()
  const [errors, setErrors] = useState<TVerifyDepositFormErrors>({})
  const dispatch = useDispatch()

  const onCancel = useCallback(() => {
    actions.close()
    setErrors({})
    form.resetFields()
  }, [actions, form])

  const onVerify = useCallback(
    async (values: TVerifyDepositForm) => {
      try {
        dispatch(setLoading(false))
        await verifyDeposit(business_account_id, uuid, values)
        onSuccess()
      } catch (error) {
        const typedResponse = error as AxiosResponse
        if (typedResponse.data.details) {
          notification.error({
            message: typedResponse.data.details
          })
          // onSuccess should be called here to refresh the table data / bank account details and show new status
          onSuccess()
        } else {
          setErrors(typedResponse.data)
        }
      } finally {
        dispatch(setLoading(false))
        onCancel()
      }
    },
    [business_account_id, dispatch, onCancel, onSuccess, uuid]
  )

  return (
    <Popup
      {...state}
      width={442}
      zIndex={10000}
      onCancel={onCancel}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={onCancel} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            upperCase
            form="verify-deposit-form"
            htmlType="submit"
          >
            {BTN_TXT.VERIFY}
          </Button>
        </>
      }
    >
      <Typography.Body1>
        We've sent two micro-deposits with the description 'AcctVerify' to your
        account ending in ****{account_number}. Please, enter the exact amounts
        (e.g., 0.12 and 0.07).
      </Typography.Body1>
      <div className="column gap-16">
        <CustomForm<TVerifyDepositForm>
          form={form}
          id="verify-deposit-form"
          onFinish={onVerify}
          onFieldsChange={() => setErrors({})}
          validateMessages={validateMessages}
        >
          <div className="row mt-24 two-columns gap-24">
            <Input.Number
              propsItem={{
                name: 'deposit_amount_1',
                help: errors.deposit_amount_1?.[0],
                validateStatus: errors.deposit_amount_1 && 'error',
                label: LABELS.DEPOSIT_AMOUNT,
                rules: [
                  {
                    required: true,
                    message: VALIDATION_MESSAGES.ENTER_BOTH_DEPOSIT_AMOUNTS
                  }
                ]
              }}
              propsInputNumber={{
                type: 'number',
                min: '0',
                max: '0.99',
                parser: twoDecimalParser,
                controls: false
              }}
            />
            <Input.Number
              propsItem={{
                name: 'deposit_amount_2',
                help: errors.deposit_amount_2?.[0],
                validateStatus: errors.deposit_amount_2 && 'error',
                label: LABELS.DEPOSIT_AMOUNT,
                rules: [
                  {
                    required: true,
                    message: VALIDATION_MESSAGES.ENTER_BOTH_DEPOSIT_AMOUNTS
                  }
                ]
              }}
              propsInputNumber={{
                type: 'number',
                min: '0',
                max: '0.99',
                parser: twoDecimalParser,
                controls: false
              }}
            />
          </div>
        </CustomForm>
        <Banner.Info text="You have 30 days and 5 attempts to verify the amounts." />
      </div>
    </Popup>
  )
}
